import { Suspense, type ReactNode } from 'react';
import ui from '../../components/ui.module.css';
import { SiteFooter } from '../chrome/SiteFooter';
import { SiteHeader } from '../chrome/SiteHeader';
import { shared } from '../content/shared';
import type { Cta } from '../content/types';
import s from '../site.module.css';

/** The shell every public page shares: skip link, header, one <main>, footer. */
export function SiteLayout({ children }: { children: ReactNode }) {
  return (
    <div className={s.page}>
      <a className="skip-link" href="#content">
        {shared.skipLink}
      </a>
      <Suspense>
        <SiteHeader />
      </Suspense>
      <main id="content" className={s.main} tabIndex={-1}>
        {children}
      </main>
      <Suspense>
        <SiteFooter />
      </Suspense>
    </div>
  );
}

/** Section grounds: light (mist, white, sky) and dark (navy, and image: the network backdrop under a navy overlay). */
type Tone = 'mist' | 'white' | 'sky' | 'navy' | 'image';

const TONES: Record<Tone, string | undefined> = { mist: '', white: s.sectionWhite, sky: s.sectionSky, navy: s.sectionNavy, image: s.sectionImage };

/**
 * A page section. Its heading is the section's h2, so headings stay in order: one h1 in the hero, then h2s. Each
 * section is its own Suspense boundary, so React hydrates the page in pieces and yields to the browser in between
 * instead of blocking the main thread for the whole page at once.
 */
export function Section({ id, tone = 'mist', labelledBy, children }: { id?: string; tone?: Tone; labelledBy: string; children: ReactNode }) {
  return (
    <section id={id} className={`${s.section} ${TONES[tone] ?? ''}`} aria-labelledby={labelledBy}>
      <Suspense>
        <div className={s.container}>{children}</div>
      </Suspense>
    </section>
  );
}

/** A call-to-action link styled as the portal's button. Links stay links. On a dark ground the secondary is sky. */
export function CtaLink({ cta, primary, onDark }: { cta: Cta; primary?: boolean; onDark?: boolean }) {
  const look = primary ? ui.primary : `${ui.secondary} ${onDark ? s.ctaOnDark : ''}`;
  return (
    <a href={cta.href} className={`${ui.button} ${look}`}>
      {cta.label}
    </a>
  );
}

export function CtaRow({ primary, secondary, onDark }: { primary: Cta; secondary?: Cta; onDark?: boolean }) {
  return (
    <div className={s.ctaRow}>
      <CtaLink cta={primary} primary />
      {secondary && <CtaLink cta={secondary} onDark={onDark} />}
    </div>
  );
}
